import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { PLATFORM_LANGUAGES, type PlatformLanguage } from '../config/reference-data';

// Mirrors backend's PlatformLanguageResponseDto + mobile's
// `PlatformLanguage` interface in mobile/src/stores/platform-languages-store.ts.
// Single source of truth for which languages the picker should offer is
// the `/platform-languages/active` endpoint — the same endpoint mobile
// hits at app boot.
export interface ActivePlatformLanguage {
  readonly code: string;
  readonly name: string;
  readonly nativeName: string;
  readonly priority: number;
}

// View-model used by the UI: backend fields + the flag emoji we already
// keep in reference-data.ts (backend doesn't ship flags).
export interface ActivePlatformLanguageView {
  readonly code: string;
  readonly name: string;
  readonly native: string;
  readonly flag: string;
  readonly priority: number;
}

const FLAG_BY_CODE: Readonly<Record<string, string>> = PLATFORM_LANGUAGES.reduce(
  (acc, l) => {
    (acc as Record<string, string>)[l.code] = l.flag;
    return acc;
  },
  {} as Record<string, string>,
);

export function useActivePlatformLanguagesQuery() {
  return useQuery({
    queryKey: queryKeys.platformLanguages.active,
    queryFn: async () => {
      const result = await apiClient.get<{ data: ActivePlatformLanguage[] }>(
        '/platform-languages/active',
      );
      return result.data.data;
    },
    // Active-language taxonomy changes rarely — keep the response warm
    // for the whole session. Mobile dedups via an in-flight promise; on
    // web we get the equivalent for free from React Query's cache.
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

// Returns languages joined with the local flag map. While the request
// is in flight (or if it fails — e.g. user hits onboarding offline), we
// fall back to the bundled PLATFORM_LANGUAGES so the picker still
// renders something. Once the active list lands, only those codes are
// shown — matching mobile's behaviour exactly.
export function useActivePlatformLanguageOptions(): readonly ActivePlatformLanguageView[] {
  const query = useActivePlatformLanguagesQuery();

  return useMemo(() => {
    const view: ActivePlatformLanguageView[] =
      query.data && query.data.length > 0
        ? query.data.map((l) => ({
            code: l.code,
            name: l.name,
            native: l.nativeName,
            flag: FLAG_BY_CODE[l.code] ?? '🌐',
            priority: l.priority,
          }))
        : PLATFORM_LANGUAGES.map((l, idx) => ({
            code: l.code,
            name: l.name,
            native: l.native,
            flag: l.flag,
            priority: idx,
          }));
    return view.sort((a, b) => a.name.localeCompare(b.name));
  }, [query.data]);
}

// Replacement for `findLanguage(code)` from reference-data.ts that
// resolves names off the active list when available.
export function findActiveLanguage(
  options: readonly ActivePlatformLanguageView[],
  code: string | null | undefined,
): ActivePlatformLanguageView | PlatformLanguage {
  return (
    options.find((l) => l.code === code) ??
    PLATFORM_LANGUAGES.find((l) => l.code === code) ??
    PLATFORM_LANGUAGES[0]!
  );
}

// Known languages — the curated ~30-entry list a user can declare as
// "I read this natively". Independent from /platform-languages/active.
// Mirrors backend's PlatformLanguageResponseDto.
export function useKnownLanguagesQuery() {
  return useQuery({
    queryKey: queryKeys.platformLanguages.known,
    queryFn: async () => {
      const result = await apiClient.get<{ data: ActivePlatformLanguage[] }>(
        '/platform-languages/known',
      );
      return result.data.data;
    },
    // Curated constant on the server — safe to keep warm for the whole
    // session.
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useKnownLanguageOptions(): readonly ActivePlatformLanguageView[] {
  const query = useKnownLanguagesQuery();
  return useMemo(() => {
    if (!query.data || query.data.length === 0) return [];
    return query.data
      .map<ActivePlatformLanguageView>((l) => ({
        code: l.code,
        name: l.name,
        native: l.nativeName,
        flag: FLAG_BY_CODE[l.code] ?? '🌐',
        priority: l.priority,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [query.data]);
}
